(() => {
  const normalize = (value) =>
    (value || '').trim().toLowerCase();

  /*
   * Get the currently selected Color option.
   */
  function getSelectedColor(section) {
    if (!section) return '';

    // Radio / button picker
    for (const fieldset of section.querySelectorAll('fieldset')) {
      const legend = fieldset.querySelector('legend');

      if (!legend) continue;

      const legendText = normalize(legend.textContent);

      /*
       * Handles:
       * "Color"
       * "Color:"
       * "COLOR"
       */
      if (
        legendText !== 'color' &&
        !legendText.startsWith('color:')
      ) {
        continue;
      }

      const checked = fieldset.querySelector(
        'input[type="radio"]:checked'
      );

      if (checked) {
        return normalize(checked.value);
      }
    }

    // Dropdown picker
    for (const select of section.querySelectorAll('select')) {
      const label = section.querySelector(
        `label[for="${select.id}"]`
      );

      if (
        label &&
        normalize(label.textContent).includes('color')
      ) {
        return normalize(select.value);
      }
    }

    return '';
  }

  /*
   * Check whether a media item belongs to
   * the currently selected color.
   *
   * Media without data-media-color is universal
   * and will always remain visible.
   */
  function matchesColor(element, color) {
    const mediaColor = normalize(
      element.dataset.mediaColor
    );

    if (!mediaColor) {
      return true;
    }

    return mediaColor === color;
  }

  /*
   * Apply the selected color to the gallery.
   *
   * This file owns ONLY media visibility.
   *
   * media-gallery.js owns:
   * - active image
   * - slider position after filtering
   * - dots
   * - active dot
   * - thumbnails
   */
  function applyColorFilter(section, force = false) {
    const color = getSelectedColor(section);

    if (!color) return;

    const gallery = section.querySelector(
      'media-gallery'
    );

    if (!gallery) return;

    /*
     * If something like Size changes but Color
     * stays the same, do not reset the gallery.
     */
    if (
      !force &&
      section.dataset.currentMediaColor === color
    ) {
      return;
    }

    section.dataset.currentMediaColor = color;

    const slides = Array.from(
      gallery.querySelectorAll(
        '.product__media-list > .product__media-item[data-media-id]'
      )
    );

    if (!slides.length) return;

    /*
     * Filter main product media.
     */
    slides.forEach((slide) => {
      const visible = matchesColor(
        slide,
        color
      );

      slide.hidden = !visible;

      slide.style.display = visible
        ? ''
        : 'none';
    });

    /*
     * Filter thumbnails.
     */
    const thumbnails = Array.from(
      gallery.querySelectorAll(
        '.thumbnail-list > .thumbnail-list__item[data-target]'
      )
    );

    thumbnails.forEach((thumbnail) => {
      const visible = matchesColor(
        thumbnail,
        color
      );

      thumbnail.hidden = !visible;

      thumbnail.style.display = visible
        ? ''
        : 'none';
    });

    /*
     * Find the first visible image for
     * the newly selected color.
     */
    const firstVisible = slides.find(
      (slide) =>
        !slide.hidden &&
        slide.style.display !== 'none'
    );

    if (!firstVisible) return;

    /*
     * Filtering is complete.
     *
     * Hand control back to media-gallery.js.
     *
     * media-gallery.js will:
     * - activate the first visible image
     * - recalculate the slider
     * - rebuild the dots
     * - activate dot #1
     * - update thumbnail state
     */
    gallery.dispatchEvent(
      new CustomEvent(
        'variant-media-filtered',
        {
          detail: {
            mediaId:
              firstVisible.dataset.mediaId,
          },
        }
      )
    );
  }

  /*
   * Determine whether the control that changed
   * is specifically the Color option.
   */
  function isColorControl(section, target) {
    if (!target) return false;

    /*
     * Radio / button picker.
     */
    if (
      target.matches(
        'input[type="radio"]'
      )
    ) {
      const fieldset =
        target.closest('fieldset');

      if (!fieldset) return false;

      const legend =
        fieldset.querySelector('legend');

      if (!legend) return false;

      const legendText = normalize(
        legend.textContent
      );

      return (
        legendText === 'color' ||
        legendText.startsWith('color:')
      );
    }

    /*Dropdown picker*/
    if (target.matches('select')) {
      const label = section.querySelector(
        `label[for="${target.id}"]`
      );

      return (
        label &&
        normalize(
          label.textContent
        ).includes('color')
      );
    }

    return false;
  }

  /*
   * Initialize one Shopify product section.
   */
  function initializeSection(section) {
    if (
      section.dataset
        .variantMediaColorFilterInitialized
    ) {
      return;
    }

    const gallery = section.querySelector(
      'media-gallery'
    );

    if (!gallery) return;

    section.dataset
      .variantMediaColorFilterInitialized =
      'true';

    /*
     * Initial page load.
     */
    applyColorFilter(
      section,
      true
    );

    /*
     * COLOR CHANGE
     *
     * Only Color changes should reset/filter
     * the gallery.
     *
     * Size, Pack, etc. should leave the
     * current gallery position untouched.
     */
    section.addEventListener(
      'change',
      (event) => {
        if (
          !isColorControl(
            section,
            event.target
          )
        ) {
          return;
        }

        /*
         * Run immediately.
         *
         * The newly selected radio/dropdown
         * value is already available here.
         */
        applyColorFilter(
          section,
          false
        );
      }
    );

    /*
     * PRODUCT-INFO UPDATE
     *
     * product-info.js fires this after Dawn
     * completes its variant media synchronization.
     *
     * Reapply visibility because Dawn may have
     * reordered or replaced gallery elements.
     */
    gallery.addEventListener(
      'variant-media-updated',
      () => {
        applyColorFilter(
          section,
          true
        );
      }
    );
  }

  /*
   * Initialize all product sections.
   */
  function initializeAllSections() {
    document
      .querySelectorAll('.shopify-section')
      .forEach((section) => {
        initializeSection(section);
      });
  }

  /*
   * Normal storefront load.
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAllSections
    );
  } else {
    initializeAllSections();
  }

  /*
   * Shopify Theme Editor support.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeSection(event.target);
    }
  );
})();